/**
 * Client-facing quote view at ``/q/{token}``.
 *
 * No auth — anyone with the link sees it. The token's the access
 * control: 32-byte url-safe random, hashed in the DB. Hidden line
 * items never reach this surface (filtered server-side).
 *
 * Lifecycle:
 *   1. Mount → GET /q/{token} (records view + returns payload)
 *   2. Client clicks "Accept" → modal with typed-name + checkbox →
 *      POST /q/{token}/sign → status flips to ``accepted``
 *   3. Client clicks "Decline" → optional reason → POST .../decline
 *
 * Print:
 *   "Print / save as PDF" button just calls window.print(). The
 *   ``@media print`` block in index.css strips chrome (footer, action
 *   buttons) so the print version is clean.
 */

import { Check, Printer, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, apiBase } from "@/api/client";
import { publicApiClient } from "@/api/publicClient";
import {
  LINE_ITEM_SECTION_LABELS,
  LINE_ITEM_SECTION_ORDER,
  type LineItemSection,
  type PublicQuoteResponse,
  type QuoteCommentResponse,
} from "@/api/types";
import { CommentThread } from "@/components/CommentThread";
import { formatCents, formatTime } from "@/lib/format";

export function PublicQuoteView() {
  const { token = "" } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuoteResponse | null>(null);
  const [comments, setComments] = useState<QuoteCommentResponse[]>([]);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [postingComment, setPostingComment] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // The mobile-only sticky bottom bar repeats the SignatureBlock's
  // accept/decline affordance so the homeowner can act without
  // scrolling all the way down past line items + terms. Triggers
  // a state on the page that auto-scrolls to (and expands) the
  // SignatureBlock — which is also where the typed-name + checkbox
  // live for the accept flow.
  //
  // CRITICAL: this useState lives at the TOP of the component
  // alongside the other hooks. The previous version declared it
  // below the early-return branches for ``!loaded`` and
  // ``loadError || !quote``, which violates React's Rules of
  // Hooks (hook count must be stable across renders). When the
  // quote actually loaded successfully the hook count jumped
  // from 6 → 7 and React threw — Sentry's error boundary caught
  // the throw and showed "Something went wrong."
  const [signatureMode, setSignatureMode] = useState<"idle" | "accept" | "decline">("idle");

  const refreshComments = useCallback(async () => {
    if (!token) return;
    try {
      const next = await publicApiClient.get<QuoteCommentResponse[]>(
        `/q/${encodeURIComponent(token)}/comments`,
      );
      setComments(next);
    } catch {
      /* keep stale list silently */
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    publicApiClient
      .get<PublicQuoteResponse>(`/q/${encodeURIComponent(token)}`)
      .then((data) => {
        if (!cancelled) {
          setQuote(data);
          setLoaded(true);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError
            ? err.detail
            : "We couldn't load this quote. The link may be invalid or expired.",
        );
        setLoaded(true);
      });
    void refreshComments();
    return () => {
      cancelled = true;
    };
  }, [token, refreshComments]);

  // Rewrite the browser tab title once the quote loads so the
  // homeowner sees "Quote from {contractor}" instead of the
  // bundle's default "PencilOut" placeholder. The tab is the
  // first thing visible across pinned/back-grounded tabs — making
  // it contextual matters more here than elsewhere in the SPA.
  useEffect(() => {
    if (!quote) return;
    const contractorName = quote.contractor?.name ?? "";
    const job = quote.job_title ? ` — ${quote.job_title}` : "";
    document.title = contractorName ? `Quote from ${contractorName}${job}` : `Quote${job}`;
    return () => {
      // Reset on unmount so navigation back to the SPA doesn't
      // strand "Quote from X" in the tab.
      document.title = "PencilOut";
    };
  }, [quote]);

  // Light polling so the client sees contractor replies arrive
  // without a hard refresh. 30s tracks the contractor side.
  useEffect(() => {
    if (!token) return;
    const handle = setInterval(() => void refreshComments(), 30_000);
    return () => clearInterval(handle);
  }, [token, refreshComments]);

  async function postComment({
    body,
    parentId,
    files,
  }: {
    body: string;
    parentId?: string | null;
    files?: File[];
  }) {
    setPostingComment(true);
    setCommentError(null);
    try {
      let created: QuoteCommentResponse;
      if (files && files.length > 0) {
        // Multipart path — body field is still required server-side.
        // Don't set Content-Type ourselves; the browser will fill in
        // the correct ``multipart/form-data; boundary=…`` header.
        const form = new FormData();
        form.append("body", body);
        if (parentId) form.append("parent_comment_id", parentId);
        for (const file of files) {
          form.append("attachments", file, file.name);
        }
        created = await publicApiClient.postMultipart<QuoteCommentResponse>(
          `/q/${encodeURIComponent(token)}/comments`,
          form,
        );
      } else {
        // Text-only path — preserve the existing JSON shape so
        // older API behavior keeps working byte-for-byte.
        created = await publicApiClient.post<QuoteCommentResponse>(
          `/q/${encodeURIComponent(token)}/comments`,
          { body, parent_comment_id: parentId ?? null },
        );
      }
      setComments((prev) => [...prev, created]);
    } catch (err) {
      setCommentError(err instanceof ApiError ? err.detail : "Could not post comment.");
      throw err;
    } finally {
      setPostingComment(false);
    }
  }

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm text-graphite-soft">
        Loading quote…
      </div>
    );
  }

  if (loadError || !quote) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <span className="brand-wordmark text-3xl">PencilOut</span>
          </div>
          <div className="rounded-lg border border-rule bg-paper-warm p-6">
            <h1 className="font-mono text-lg font-bold tracking-tight">Couldn't load quote</h1>
            <p className="mt-2 text-sm text-graphite-soft">
              {loadError ?? "The link may be invalid or expired."}
            </p>
            <p className="mt-3 font-mono text-[10px] text-graphite-faint">
              Reach out to your contractor for a fresh link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-graphite print:bg-white">
      <PrintHeaderActions token={token} />

      <main
        className={
          // Pad the bottom on mobile so the sticky action bar
          // doesn't cover the last line of the comment thread.
          // Desktop / print don't need the extra room.
          "mx-auto w-full max-w-3xl px-4 py-6 pb-32 sm:px-6 sm:py-12 sm:pb-12 print:px-6 print:py-4 print:pb-4"
        }
      >
        <Letterhead quote={quote} />

        <BillingBlock quote={quote} />

        <LineItems quote={quote} />

        <Totals quote={quote} />

        {quote.change_orders && quote.change_orders.length > 0 && (
          <ChangeOrderList
            quote={quote}
            token={token}
            onUpdated={setQuote}
          />
        )}

        {quote.terms && (
          <section className="mt-8">
            <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Terms
            </h3>
            <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed sm:text-sm">
              {quote.terms}
            </p>
          </section>
        )}

        {quote.org_disclaimers && quote.org_disclaimers.length > 0 && (
          <section className="mt-8">
            <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Disclosures
            </h3>
            <div className="mt-2 space-y-3">
              {quote.org_disclaimers.map((d) => (
                <div key={d.key} className="rounded border border-rule bg-paper-warm p-3">
                  <p className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite">
                    {d.title}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-graphite-soft">
                    {d.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {quote.payment_schedule && quote.payment_schedule.length > 0 && (
          <section className="mt-8">
            <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Payment schedule
            </h3>
            <ul className="mt-2 divide-y divide-rule rounded-lg border border-rule bg-paper-warm">
              {quote.payment_schedule.map((entry, i) => (
                <li
                  key={`${entry.label}-${i}`}
                  className="flex items-center justify-between gap-4 px-4 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-semibold text-graphite">
                      {entry.label}
                      <span className="ml-2 font-mono text-xs text-graphite-soft">
                        ({Math.round(entry.percent * 100)}%)
                      </span>
                    </p>
                    {entry.trigger && (
                      <p className="font-mono text-xs text-graphite-soft">
                        {entry.trigger}
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-sm font-semibold tabular-nums">
                    {formatCents(entry.amount_cents)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {quote.org_payment_methods && quote.org_payment_methods.length > 0 && (
          <section className="mt-8">
            <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Payment methods
            </h3>
            <ul className="mt-2 space-y-1.5 text-sm">
              {quote.org_payment_methods.map((m, i) => (
                <li key={`${m.method}-${i}`} className="font-mono">
                  <span className="font-semibold capitalize">{m.method}</span>
                  {m.payable_to_or_handle && (
                    <span className="text-graphite-soft"> · {m.payable_to_or_handle}</span>
                  )}
                  {m.notes && (
                    <span className="text-graphite-faint"> — {m.notes}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <SignatureBlock
          quote={quote}
          token={token}
          onUpdated={setQuote}
          mode={signatureMode}
          onModeChange={setSignatureMode}
        />

        <div className="mt-10 print:hidden">
          <CommentThread
            comments={comments}
            // Server resolves identity from the share token (signed_name
            // when the quote was accepted, else the linked client's
            // name). Show it read-only above the compose field so the
            // homeowner knows who they appear as on the thread.
            identityName={quote.signed_name ?? quote.client?.name ?? "Client"}
            // Client can reply to a specific contractor message —
            // useful when the contractor sent multiple updates and the
            // client wants to be unambiguous about which they're
            // responding to.
            showReplyButton
            composeLabel="Question or comment"
            composePlaceholder="Ask anything — your contractor will reply"
            submitting={postingComment}
            errorMessage={commentError}
            onSubmit={postComment}
            // Resolve attachment ``download_url`` (API-relative, e.g.
            // ``/q/{token}/comments/{cid}/attachments/{aid}``) into
            // an absolute URL the browser can open / fetch.
            apiBase={apiBase}
            // Public-thread homeowner can attach photos / PDFs of
            // damage, room layouts, existing materials, etc.
            allowAttachments
          />
        </div>
      </main>

      <footer className="border-t border-rule bg-paper-warm py-4 print:hidden">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 sm:px-6">
          <span className="label-mono">Quote prepared by {quote.contractor.name}</span>
          <span className="label-mono text-graphite-faint">powered by PencilOut</span>
        </div>
      </footer>

      {/* Sticky mobile action bar — only when the quote is still
          actionable. Desktop has the inline SignatureBlock right
          there; hiding this at sm+ keeps the layout uncluttered. */}
      {quote.status !== "accepted" && quote.status !== "declined" && (
        <MobileStickyActions
          totalCents={quote.total_cents}
          onAccept={() => {
            setSignatureMode("accept");
            // Scroll the SignatureBlock into view so the typed-name +
            // checkbox are immediately visible after the user taps.
            requestAnimationFrame(() =>
              document
                .getElementById("signature-block")
                ?.scrollIntoView({ behavior: "smooth", block: "center" }),
            );
          }}
          onDecline={() => {
            setSignatureMode("decline");
            requestAnimationFrame(() =>
              document
                .getElementById("signature-block")
                ?.scrollIntoView({ behavior: "smooth", block: "center" }),
            );
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------

/** Sticky bottom bar — mobile only. Fixed at the viewport bottom
 *  so the homeowner can accept or decline without scrolling through
 *  the entire quote first. Hidden on ``sm:`` (desktop layout already
 *  has the inline SignatureBlock visible) and in print.
 *
 *  Accept here just opens the existing inline form (transparent for
 *  the contractor — same e-signature flow, same persisted state)
 *  and scrolls to it; we don't fork the accept path or duplicate
 *  the typed-name / checkbox UI. */
function MobileStickyActions({
  totalCents,
  onAccept,
  onDecline,
}: {
  totalCents: number;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <div
      className={
        // pb-[env(safe-area-inset-bottom)] respects the iPhone home
        // indicator so the buttons stay tappable above the system bar.
        "fixed inset-x-0 bottom-0 z-40 border-t-2 border-graphite bg-paper-warm shadow-[0_-4px_14px_-8px_rgba(31,27,23,0.25)] sm:hidden print:hidden"
      }
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
            Total
          </div>
          <div className="font-mono text-xl font-bold tabular-nums">{formatCents(totalCents)}</div>
        </div>
        <button
          type="button"
          onClick={onDecline}
          className="rounded border border-rule bg-paper px-3 py-2.5 font-mono text-sm font-semibold text-graphite-soft transition active:bg-red-pencil/10 active:text-red-pencil"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={onAccept}
          className="rounded bg-graphite px-4 py-2.5 font-mono text-sm font-semibold text-paper transition active:bg-graphite-soft"
        >
          Accept
        </button>
      </div>
    </div>
  );
}

function PrintHeaderActions({ token }: { token: string }) {
  return (
    <div className="border-b border-rule bg-paper-warm py-3 print:hidden">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 sm:px-6">
        <span className="brand-wordmark text-xl sm:text-2xl">PencilOut</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">Print</span>
            <span className="sm:hidden">Print</span>
          </button>
          <a
            href={`${apiBase}/q/${encodeURIComponent(token)}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">Download PDF</span>
            <span className="sm:hidden">PDF</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export function Letterhead({ quote }: { quote: PublicQuoteResponse }) {
  return (
    <header className="flex flex-col gap-4 border-b-2 border-graphite pb-6 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
      <div className="flex items-center gap-3 sm:gap-4">
        {quote.contractor.logo_url && (
          <img
            src={`${apiBase}${quote.contractor.logo_url}`}
            alt={`${quote.contractor.name} logo`}
            className="h-14 w-auto max-w-[140px] flex-none object-contain sm:h-16 sm:max-w-[180px]"
          />
        )}
        <div className="min-w-0">
          {/* Bumped from text-xl on phones (was 20px → too small for
              a brand wordmark on a quote document); desktop stays
              text-3xl unchanged. */}
          <h1 className="font-mono text-2xl font-bold tracking-tight sm:text-3xl">
            {quote.contractor.name}
          </h1>
          {quote.contractor.sender_name && (
            <p className="mt-1 font-mono text-sm text-graphite-soft sm:mt-1 sm:text-xs">
              Prepared by {quote.contractor.sender_name}
            </p>
          )}
        </div>
      </div>
      <div className="sm:text-right">
        <span className="label-mono">Quote</span>
        {/* Quote-ID font-size bumped on phones — it's a document
            identifier and warrants a hair more presence. */}
        <p className="mt-0.5 font-mono text-base tabular-nums sm:mt-1 sm:text-sm">
          #{quote.id.slice(0, 8).toUpperCase()}
        </p>
        {quote.sent_at && (
          <p className="mt-1.5 font-mono text-xs text-graphite-soft sm:mt-2">
            Sent {formatTime(quote.sent_at)}
          </p>
        )}
        {quote.valid_until && (
          <p className="font-mono text-xs text-graphite-soft">
            Valid through {formatTime(quote.valid_until)}
          </p>
        )}
      </div>
    </header>
  );
}

export function BillingBlock({ quote }: { quote: PublicQuoteResponse }) {
  // In artisan mode, the project description IS the scope of work —
  // there's no line-item table backing it up. So when artisan, we
  // pull project narrative out into its own full-width section
  // below and keep this block focused on Bill-To + project name.
  // Itemized mode keeps the legacy two-column layout where the
  // description shares the row with Bill-To.
  const isArtisan = quote.render_mode === "artisan";

  return (
    <>
      <section className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <span className="label-mono">For</span>
          {quote.client ? (
            <div className="mt-1.5 text-base leading-relaxed sm:text-base">
              <div className="font-medium">{quote.client.name}</div>
              {quote.client.address_line1 && <div>{quote.client.address_line1}</div>}
              {quote.client.address_line2 && <div>{quote.client.address_line2}</div>}
              {(quote.client.city || quote.client.region || quote.client.postal_code) && (
                <div>
                  {[quote.client.city, quote.client.region].filter(Boolean).join(", ")}
                  {quote.client.postal_code && ` ${quote.client.postal_code}`}
                </div>
              )}
              {quote.client.email && (
                <div className="mt-1 font-mono text-sm text-graphite-soft sm:text-xs">
                  {quote.client.email}
                </div>
              )}
            </div>
          ) : (
            <p className="mt-1 text-graphite-faint">—</p>
          )}
        </div>
        <div>
          <span className="label-mono">Project</span>
          <div className="mt-1.5 text-base leading-relaxed sm:text-base">
            <div className="font-medium">{quote.job_title}</div>
            {!isArtisan && quote.job_description && (
              <div className="mt-1.5 text-sm text-graphite-soft">{quote.job_description}</div>
            )}
          </div>
        </div>
      </section>

      {isArtisan && quote.job_description && (
        <section className="mt-8">
          <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Scope of work
          </h3>
          <p className="mt-3 whitespace-pre-wrap text-base leading-relaxed text-graphite">
            {quote.job_description}
          </p>
        </section>
      )}
    </>
  );
}

/** Group visible line items into the canonical 18-section A–R
 *  template. Returns ALL 18 sections in order, with each section
 *  carrying its lines (possibly empty). The renderer then shows
 *  empty sections as "No action required in this section" — the
 *  pattern that signals to the homeowner what's NOT included by
 *  being explicit about scope coverage. Lines without a
 *  ``section_code`` (legacy or contractor-skipped) collect into a
 *  "_unsectioned" bucket the caller renders separately above the
 *  18-section grid. */
function groupLinesBySection(quote: PublicQuoteResponse): {
  sectioned: ReadonlyArray<{
    code: LineItemSection;
    label: string;
    items: PublicQuoteResponse["line_items"];
  }>;
  unsectioned: PublicQuoteResponse["line_items"];
} {
  const bySection = new Map<LineItemSection, PublicQuoteResponse["line_items"]>();
  const unsectioned: PublicQuoteResponse["line_items"] = [];
  for (const li of quote.line_items) {
    if (!li.section_code) {
      unsectioned.push(li);
      continue;
    }
    const existing = bySection.get(li.section_code as LineItemSection) ?? [];
    existing.push(li);
    bySection.set(li.section_code as LineItemSection, existing);
  }
  const sectioned = LINE_ITEM_SECTION_ORDER.map((code) => ({
    code,
    label: LINE_ITEM_SECTION_LABELS[code],
    items: bySection.get(code) ?? [],
  }));
  return { sectioned, unsectioned };
}

/** Group visible line items by ``room`` (free-form label). Returns
 *  rooms in first-seen order so the contractor's ordering is
 *  preserved. Lines without a room go into a single ``""`` bucket
 *  the caller can render under a generic header. Used by the
 *  per-room rollup in itemized mode. */
function groupLinesByRoom(quote: PublicQuoteResponse): ReadonlyArray<{
  room: string;
  items: PublicQuoteResponse["line_items"];
}> {
  const order: string[] = [];
  const buckets = new Map<string, PublicQuoteResponse["line_items"]>();
  for (const li of quote.line_items) {
    const key = li.room ?? "";
    if (!buckets.has(key)) {
      order.push(key);
      buckets.set(key, []);
    }
    buckets.get(key)!.push(li);
  }
  return order.map((room) => ({ room, items: buckets.get(room) ?? [] }));
}

/** Sectional render: A–R sections in order. Empty sections show
 *  "No action required in this section". Lines without a section
 *  fall under an "Additional scope" bucket above the grid so they
 *  don't disappear silently. */
function SectionalLineItems({ quote }: { quote: PublicQuoteResponse }) {
  const { sectioned, unsectioned } = groupLinesBySection(quote);
  return (
    <section className="mt-8">
      <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Scope of work
      </h3>

      {unsectioned.length > 0 && (
        <div className="mt-3 rounded border border-rule bg-paper-warm p-4">
          <p className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Additional scope
          </p>
          <ul className="mt-2 space-y-2">
            {unsectioned.map((li) => (
              <SectionalLineRow key={li.ordinal} li={li} />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {sectioned.map((section) => (
          <div key={section.code}>
            <h4 className="font-mono text-sm font-semibold tracking-tight text-graphite">
              {section.label}
            </h4>
            {section.items.length === 0 ? (
              <p className="mt-1 text-sm italic text-graphite-soft">
                No action required in this section.
              </p>
            ) : (
              <ul className="mt-2 space-y-2 border-l-2 border-rule pl-4">
                {section.items.map((li) => (
                  <SectionalLineRow key={li.ordinal} li={li} />
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** One line in a sectional render. Shows description + kind badge +
 *  per-room label + optional assumptions; price only on the right
 *  for non-TBD lines. Compact-mobile and tablet+ share the same
 *  shape — sections are vertically dense by nature. */
function SectionalLineRow({ li }: { li: PublicQuoteResponse["line_items"][number] }) {
  const kind = li.kind ?? "firm";
  const isTbd = kind === "tbd";
  return (
    <li className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="leading-snug">
          {li.description}
          <KindBadge kind={kind} />
          {li.room && (
            <span className="ml-2 align-middle font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
              · {li.room}
            </span>
          )}
        </div>
        {li.assumptions && (
          <p className="mt-0.5 text-xs italic text-graphite-soft">{li.assumptions}</p>
        )}
      </div>
      <div className="font-mono text-sm tabular-nums">
        {isTbd ? (
          <span className="text-graphite-soft">Pending</span>
        ) : (
          formatCents(li.total_cents)
        )}
      </div>
    </li>
  );
}

/** Per-room rollup. Every distinct ``room`` value gets its own
 *  block with a sub-total at the bottom. TBD lines render but
 *  are excluded from the room subtotal (mirrors server math).
 *  Lines with no room appear under "Other scope" so nothing
 *  silently disappears. */
function RoomGroupedLineItems({ quote }: { quote: PublicQuoteResponse }) {
  const groups = groupLinesByRoom(quote);
  return (
    <section className="mt-8">
      <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Scope of work
      </h3>
      <div className="mt-4 space-y-6">
        {groups.map((group) => {
          const roomLabel = group.room || "Other scope";
          const subtotal = group.items
            .filter((li) => (li.kind ?? "firm") !== "tbd")
            .reduce((sum, li) => sum + li.total_cents, 0);
          return (
            <div key={roomLabel}>
              <div className="flex items-baseline justify-between border-b border-rule pb-2">
                <h4 className="font-mono text-base font-bold tracking-tight text-graphite">
                  {roomLabel}
                </h4>
                <span className="font-mono text-sm tabular-nums text-graphite-soft">
                  {formatCents(subtotal)}
                </span>
              </div>
              <ul className="mt-2 space-y-2">
                {group.items.map((li) => (
                  <SectionalLineRow key={li.ordinal} li={li} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Change orders panel on the public quote. Pending COs (status
 *  = sent) get inline Approve / Decline buttons. Decided COs
 *  (accepted / declined) render as audit history. The homeowner
 *  reaches every CO through the parent quote's share token — no
 *  separate URL per change order. */
export function ChangeOrderList({
  quote,
  token,
  onUpdated,
  readOnly = false,
}: {
  quote: PublicQuoteResponse;
  token: string;
  onUpdated: (q: PublicQuoteResponse) => void;
  /** When true, suppresses Approve / Decline buttons on pending
   *  COs. Used by the contractor's customer-preview view — they
   *  shouldn't be able to sign their own preview, but they DO want
   *  to see what the homeowner will see (pending banner +
   *  status). */
  readOnly?: boolean;
}) {
  const items = quote.change_orders ?? [];
  const pending = items.filter((co) => co.status === "sent");
  const decided = items.filter((co) => co.status !== "sent");

  return (
    <section className="mt-8">
      <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Change orders
      </h3>

      {pending.length > 0 && (
        <p className="mt-2 rounded border-2 border-pencil-yellow bg-pencil-yellow-soft p-3 text-sm text-graphite">
          <strong>
            {pending.length} change order{pending.length === 1 ? "" : "s"}{" "}
            pending your approval.
          </strong>{" "}
          Review the details below, then approve or decline. Approving
          updates your project total + adds a signed amendment to the
          original quote.
        </p>
      )}

      <ul className="mt-3 space-y-3">
        {pending.map((co) =>
          readOnly ? (
            <DecidedChangeOrderRow key={co.id} co={co} />
          ) : (
            <PendingChangeOrderRow
              key={co.id}
              co={co}
              token={token}
              onUpdated={async () => {
                // Refetch the public quote so we see the new statuses.
                try {
                  const next = await publicApiClient.get<PublicQuoteResponse>(
                    `/q/${encodeURIComponent(token)}`,
                  );
                  onUpdated(next);
                } catch {
                  /* keep local state — user can refresh manually */
                }
              }}
            />
          ),
        )}
        {decided.map((co) => (
          <DecidedChangeOrderRow key={co.id} co={co} />
        ))}
      </ul>
    </section>
  );
}

function PendingChangeOrderRow({
  co,
  token,
  onUpdated,
}: {
  co: PublicQuoteResponse["change_orders"] extends infer T
    ? T extends Array<infer U>
      ? U
      : never
    : never;
  token: string;
  onUpdated: () => void;
}) {
  const [mode, setMode] = useState<"idle" | "accept" | "decline">("idle");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isCredit = co.amount_cents < 0;

  async function submitAccept() {
    if (!name.trim() || !agreed) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await publicApiClient.post(
        `/q/${encodeURIComponent(token)}/change-orders/${co.id}/sign`,
        { name: name.trim() },
      );
      onUpdated();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not sign.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDecline() {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      await publicApiClient.post(
        `/q/${encodeURIComponent(token)}/change-orders/${co.id}/decline`,
        { reason: reason.trim() || null },
      );
      onUpdated();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not decline.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="rounded-lg border border-pencil-yellow/40 bg-paper-warm p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
              CO #{co.number}
            </span>
            <p className="text-base font-semibold text-graphite">{co.title}</p>
          </div>
          {co.description && (
            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-graphite-soft">
              {co.description}
            </p>
          )}
        </div>
        <span
          className={`font-mono text-base font-bold tabular-nums ${
            isCredit ? "text-burnt" : "text-graphite"
          }`}
        >
          {isCredit ? "−" : "+"}
          {formatCents(Math.abs(co.amount_cents))}
        </span>
      </div>

      {mode === "idle" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setMode("accept")}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => setMode("decline")}
            className="rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite hover:bg-red-pencil/10 hover:text-red-pencil"
          >
            Decline
          </button>
        </div>
      )}

      {mode === "accept" && (
        <div className="mt-4 rounded border border-graphite bg-paper p-3 space-y-3">
          <p className="font-mono text-xs text-graphite-soft">
            Type your name to sign. We'll record the signature with your IP +
            timestamp as a binding e-signature under the US E-SIGN Act.
          </p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="w-full rounded border border-rule bg-paper-warm px-2 py-2 text-base focus:border-graphite focus:outline-none"
          />
          <label className="flex items-start gap-2 text-sm text-graphite">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
            />
            <span>
              I authorize this change order at{" "}
              <strong>
                {isCredit ? "−" : "+"}
                {formatCents(Math.abs(co.amount_cents))}
              </strong>{" "}
              and agree to amend my project total accordingly.
            </span>
          </label>
          {errorMsg && (
            <p className="font-mono text-xs text-red-pencil">{errorMsg}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setMode("idle")}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitAccept}
              disabled={!name.trim() || !agreed || submitting}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-50"
            >
              {submitting ? "Signing…" : "Approve change order"}
            </button>
          </div>
        </div>
      )}

      {mode === "decline" && (
        <div className="mt-4 rounded border border-red-pencil/40 bg-paper p-3 space-y-3">
          <p className="font-mono text-xs text-graphite-soft">
            Optional: tell your contractor why so they can adjust scope.
          </p>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="(optional reason)"
            className="w-full rounded border border-rule bg-paper-warm px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
          {errorMsg && (
            <p className="font-mono text-xs text-red-pencil">{errorMsg}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setMode("idle")}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitDecline}
              disabled={submitting}
              className="rounded border border-red-pencil bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-red-pencil hover:bg-red-pencil/10 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Decline"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function DecidedChangeOrderRow({
  co,
}: {
  co: PublicQuoteResponse["change_orders"] extends infer T
    ? T extends Array<infer U>
      ? U
      : never
    : never;
}) {
  const isAccepted = co.status === "accepted";
  const isCredit = co.amount_cents < 0;
  const tone = isAccepted
    ? "border-pencil-yellow/40 bg-pencil-yellow-soft"
    : "border-red-pencil/30 bg-red-pencil/5";
  return (
    <li className={`rounded-lg border p-3 ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
              CO #{co.number}
            </span>
            <p className="text-sm font-semibold text-graphite">{co.title}</p>
            <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
              {co.status}
            </span>
          </div>
          <p className="mt-1 font-mono text-xs text-graphite-soft">
            {isAccepted
              ? `Signed by ${co.signed_name ?? "—"} ${formatTime(co.accepted_at)}`
              : `Declined ${formatTime(co.declined_at)}`}
            {!isAccepted && co.decline_reason && (
              <>
                {" — "}
                <em>"{co.decline_reason}"</em>
              </>
            )}
          </p>
        </div>
        <span
          className={`font-mono text-sm font-semibold tabular-nums ${
            isCredit ? "text-burnt" : "text-graphite"
          }`}
        >
          {isCredit ? "−" : "+"}
          {formatCents(Math.abs(co.amount_cents))}
        </span>
      </div>
    </li>
  );
}

function KindBadge({ kind }: { kind: "firm" | "allowance" | "client_supplied" | "tbd" }) {
  // No badge for legacy firm rows — keeps the description clean.
  // The other kinds get a small uppercase tag so the homeowner
  // immediately sees which lines are budget reservations,
  // contractor-supplied, or pending. TBD's "Pending" indicator
  // also shows up in the price column so the badge is partially
  // redundant; we still render it because mobile users may scan
  // the description-line-only view first.
  if (kind === "firm") return null;
  const label =
    kind === "allowance"
      ? "Allowance"
      : kind === "client_supplied"
        ? "Client-supplied"
        : "Pending";
  const tone =
    kind === "allowance"
      ? "bg-pencil-yellow-soft text-graphite border-pencil-yellow/40"
      : kind === "client_supplied"
        ? "bg-paper text-graphite-soft border-rule"
        : "bg-burnt/10 text-burnt border-burnt/30";
  return (
    <span
      className={`ml-2 inline-flex items-center rounded border px-1.5 py-0.5 align-middle font-mono text-[10px] font-semibold uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

export function LineItems({ quote }: { quote: PublicQuoteResponse }) {
  // Artisan mode: hide the line-item table entirely. The homeowner
  // sees only the project description (rendered in BillingBlock) +
  // the grand total (rendered in Totals). Mirrors how experienced
  // residential remodelers ship a bid — one price for the room,
  // scope as prose, no line-by-line visibility for the client to
  // nickel-and-dime. Internal contractor view keeps the full
  // breakdown regardless.
  if (quote.render_mode === "artisan") {
    return null;
  }

  // Sectional template mode: group lines into the canonical 18-section
  // A–R skeleton; sections without lines render a "No action required"
  // placeholder so the homeowner sees what's NOT included by being
  // explicit about scope coverage. Mirrors dad's EST0557 structural
  // skeleton.
  if (quote.use_sectional_template) {
    return <SectionalLineItems quote={quote} />;
  }

  // No sectional template — but if the contractor populated rooms,
  // group lines by room with per-room subtotals (Tier-3 per-room
  // rollup). Empty rooms (no rows with that label) never render;
  // legacy quotes with no rooms fall through to the flat table.
  const hasRooms = quote.line_items.some((li) => li.room);
  if (hasRooms) {
    return <RoomGroupedLineItems quote={quote} />;
  }

  return (
    <section className="mt-8">
      <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Scope of work
      </h3>

      {quote.line_items.length === 0 ? (
        <p className="mt-3 rounded border border-rule bg-paper-warm py-4 text-center text-sm text-graphite-soft">
          No line items.
        </p>
      ) : (
        <>
          {/* Phone: card list. Avoids tight 4-column table squashing
              the description on narrow screens. Switches to a real
              table at sm+ (and for print).
              Font sizes intentionally bigger than desktop's
              corresponding table cells — phone screens need the
              extra readability and we have the room when we're not
              also showing a Qty + Unit-rate column inline. */}
          <ul className="mt-3 space-y-3 sm:hidden print:hidden">
            {quote.line_items.map((li) => {
              const kind = li.kind ?? "firm";
              const isTbd = kind === "tbd";
              return (
                <li key={li.ordinal} className="rounded-lg border border-rule bg-paper-warm p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-base leading-snug">
                        {li.description}
                        <KindBadge kind={kind} />
                      </div>
                      <div className="mt-1.5 font-mono text-xs uppercase tracking-wider text-graphite-soft">
                        {li.category}
                      </div>
                    </div>
                    <div className="font-mono text-base font-semibold tabular-nums">
                      {isTbd ? (
                        <span className="text-graphite-soft">Pending</span>
                      ) : (
                        formatCents(li.total_cents)
                      )}
                    </div>
                  </div>
                  {!isTbd && (
                    <div className="mt-2.5 border-t border-rule/60 pt-2 font-mono text-xs tabular-nums text-graphite-soft">
                      {li.quantity} {li.unit} × {formatCents(li.unit_price_cents)}
                    </div>
                  )}
                  {li.assumptions && (
                    <p className="mt-2 text-xs italic leading-relaxed text-graphite-soft">
                      {li.assumptions}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Tablet+: full table. Also rendered for print. */}
          <table className="mt-3 hidden w-full sm:table print:table">
            <thead className="border-b border-rule">
              <tr>
                <th className="label-mono py-2 text-left">Description</th>
                <th className="label-mono py-2 text-right">Qty</th>
                <th className="label-mono py-2 text-right">Unit</th>
                <th className="label-mono py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.line_items.map((li) => {
                const kind = li.kind ?? "firm";
                const isTbd = kind === "tbd";
                return (
                  <tr key={li.ordinal} className="border-b border-rule/60 align-top">
                    <td className="py-3 pr-4">
                      <div className="text-sm">
                        {li.description}
                        <KindBadge kind={kind} />
                      </div>
                      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
                        {li.category}
                      </div>
                      {li.assumptions && (
                        <p className="mt-1 text-xs italic leading-relaxed text-graphite-soft">
                          {li.assumptions}
                        </p>
                      )}
                    </td>
                    <td className="py-3 pl-2 pr-2 text-right font-mono text-sm tabular-nums">
                      {isTbd ? "—" : li.quantity}
                    </td>
                    <td className="py-3 pl-2 pr-2 text-right font-mono text-xs text-graphite-soft tabular-nums">
                      {isTbd ? "—" : `${li.unit} @ ${formatCents(li.unit_price_cents)}`}
                    </td>
                    <td className="py-3 pl-2 text-right font-mono text-sm font-semibold tabular-nums">
                      {isTbd ? (
                        <span className="text-graphite-soft">Pending</span>
                      ) : (
                        formatCents(li.total_cents)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

export function Totals({ quote }: { quote: PublicQuoteResponse }) {
  // Client-facing — only the final Total renders. Subtotal +
  // markup are contractor-internal: showing the markup amount
  // leaks the margin, and showing subtotal alongside total
  // creates an unexplained gap (= markup amount, which we just
  // tried to hide). Line items above already itemize the work.
  return (
    <section
      className={
        "mt-6 rounded-lg border border-rule bg-paper-warm p-4 sm:ml-auto sm:max-w-xs sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 print:rounded-none print:border-0 print:bg-transparent print:p-0"
      }
    >
      <dl className="space-y-2 font-mono text-sm tabular-nums">
        <div className="flex items-baseline justify-between border-t-2 border-graphite pt-2 font-bold">
          <dt className="text-base sm:text-lg">Total</dt>
          <dd className="text-2xl sm:text-lg">{formatCents(quote.total_cents)}</dd>
        </div>
      </dl>
    </section>
  );
}

function SignatureBlock({
  quote,
  token,
  onUpdated,
  mode,
  onModeChange,
}: {
  quote: PublicQuoteResponse;
  token: string;
  onUpdated: (q: PublicQuoteResponse) => void;
  mode: "idle" | "accept" | "decline";
  onModeChange: (m: "idle" | "accept" | "decline") => void;
}) {
  if (quote.status === "accepted") {
    return (
      <>
        <section
          id="signature-block"
          className="mt-10 rounded-lg border-2 border-pencil-yellow bg-pencil-yellow-soft p-5 print:border-graphite"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-pencil-yellow">
              <Check className="h-5 w-5 text-graphite" aria-hidden />
            </span>
            <div>
              <h3 className="font-mono text-base font-bold tracking-tight">Quote accepted</h3>
              <p className="mt-0.5 text-sm text-graphite-soft">
                Signed by <span className="font-medium text-graphite">{quote.signed_name}</span> on{" "}
                {formatTime(quote.accepted_at)}
              </p>
            </div>
          </div>
        </section>

        <ProjectPortal quote={quote} />
      </>
    );
  }

  if (quote.status === "declined") {
    return (
      <section
        id="signature-block"
        className="mt-10 rounded-lg border-2 border-red-pencil/40 bg-red-pencil/5 p-5"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-red-pencil/10">
            <X className="h-5 w-5 text-red-pencil" aria-hidden />
          </span>
          <div>
            <h3 className="font-mono text-base font-bold tracking-tight">Quote declined</h3>
            <p className="mt-0.5 text-sm text-graphite-soft">
              Declined on {formatTime(quote.declined_at)}
            </p>
            {quote.decline_reason && (
              <p className="mt-2 text-sm italic text-graphite">"{quote.decline_reason}"</p>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Active state — show accept / decline buttons.
  return (
    <ActiveSignatureBlock
      token={token}
      onUpdated={onUpdated}
      clientName={quote.client?.name}
      mode={mode}
      onModeChange={onModeChange}
    />
  );
}

function ActiveSignatureBlock({
  token,
  onUpdated,
  clientName,
  mode,
  onModeChange,
}: {
  token: string;
  onUpdated: (q: PublicQuoteResponse) => void;
  clientName: string | undefined;
  mode: "idle" | "accept" | "decline";
  onModeChange: (m: "idle" | "accept" | "decline") => void;
}) {
  const [name, setName] = useState(clientName ?? "");
  const [agreed, setAgreed] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function submitAccept() {
    if (!name.trim() || !agreed) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const updated = await publicApiClient.post<PublicQuoteResponse>(
        `/q/${encodeURIComponent(token)}/sign`,
        { name: name.trim() },
      );
      onUpdated(updated);
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not submit signature.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDecline() {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const updated = await publicApiClient.post<PublicQuoteResponse>(
        `/q/${encodeURIComponent(token)}/decline`,
        { reason: reason.trim() || null },
      );
      onUpdated(updated);
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not decline.");
    } finally {
      setSubmitting(false);
    }
  }

  if (mode === "idle") {
    return (
      <section
        id="signature-block"
        className="mt-10 rounded-lg border-2 border-graphite bg-paper-warm p-5 print:hidden"
      >
        <h3 className="font-mono text-base font-bold tracking-tight sm:text-base">
          Ready to decide?
        </h3>
        <p className="mt-1 text-sm text-graphite-soft">
          Accept this quote to authorize the work, or decline if it's not the right fit. You can
          also reach out to your contractor with questions before deciding.
        </p>
        {/* Stacked + full-width on mobile, side-by-side on desktop —
            the Accept button is the page's primary action so it
            should dominate the phone-screen viewport. */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={() => onModeChange("accept")}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded bg-graphite px-4 py-3 font-mono text-base font-semibold text-paper transition hover:bg-graphite-soft sm:w-auto sm:py-2 sm:text-sm"
          >
            <ThumbsUp className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden /> Accept this quote
          </button>
          <button
            type="button"
            onClick={() => onModeChange("decline")}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded border border-rule bg-paper px-4 py-3 font-mono text-base font-semibold text-graphite transition hover:bg-red-pencil/10 hover:text-red-pencil sm:w-auto sm:py-2 sm:text-sm"
          >
            <ThumbsDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden /> Decline
          </button>
        </div>
      </section>
    );
  }

  if (mode === "accept") {
    return (
      <section
        id="signature-block"
        className="mt-10 rounded-lg border-2 border-graphite bg-paper-warm p-5 print:hidden"
      >
        <h3 className="font-mono text-base font-bold tracking-tight">Accept this quote</h3>
        <p className="mt-1 text-sm text-graphite-soft">
          Type your full name to sign. We'll record this signature with a timestamp + your IP
          address as a binding e-signature under the US E-SIGN Act.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="label-mono">Your full name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              autoComplete="name"
              // text-base prevents iOS from zooming on focus (it
              // auto-zooms when focused field font-size < 16px).
              className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-base focus:border-graphite focus:outline-none sm:text-sm"
            />
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 h-4 w-4 sm:h-3.5 sm:w-3.5"
            />
            <span className="text-sm text-graphite">
              I authorize the work described above and agree to the terms. I understand this
              constitutes a legally binding electronic signature.
            </span>
          </label>
          {errorMsg && <p className="text-sm text-red-pencil">{errorMsg}</p>}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={submitAccept}
              disabled={!name.trim() || !agreed || submitting}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded bg-graphite px-4 py-3 font-mono text-base font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50 sm:w-auto sm:py-2 sm:text-sm"
            >
              <Check className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden />
              {submitting ? "Submitting…" : "Sign and accept"}
            </button>
            <button
              type="button"
              onClick={() => {
                onModeChange("idle");
                setErrorMsg(null);
              }}
              disabled={submitting}
              className="rounded px-3 py-3 font-mono text-base text-graphite-soft hover:text-graphite disabled:opacity-50 sm:py-2 sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </section>
    );
  }

  // mode === "decline"
  return (
    <section
      id="signature-block"
      className="mt-10 rounded-lg border-2 border-graphite bg-paper-warm p-5 print:hidden"
    >
      <h3 className="font-mono text-base font-bold tracking-tight">Decline this quote</h3>
      <p className="mt-1 text-sm text-graphite-soft">
        Optional: tell your contractor why so they can adjust if it makes sense.
      </p>
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="label-mono">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Out of budget · timing doesn't work · going with another contractor · …"
            className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-base focus:border-graphite focus:outline-none sm:text-sm"
          />
        </label>
        {errorMsg && <p className="text-sm text-red-pencil">{errorMsg}</p>}
        <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={submitDecline}
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded border border-red-pencil bg-paper px-4 py-3 font-mono text-base font-semibold text-red-pencil transition hover:bg-red-pencil/10 disabled:opacity-50 sm:w-auto sm:py-2 sm:text-sm"
          >
            <ThumbsDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" aria-hidden />
            {submitting ? "Submitting…" : "Decline quote"}
          </button>
          <button
            type="button"
            onClick={() => {
              onModeChange("idle");
              setErrorMsg(null);
            }}
            disabled={submitting}
            className="rounded px-3 py-3 font-mono text-base text-graphite-soft hover:text-graphite disabled:opacity-50 sm:py-2 sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------
// ProjectPortal — post-acceptance progress view shown to the
// homeowner. Deposit receipt + project status timeline (planned
// start → in progress → complete). Renders nothing when the
// project has no portal-visible state yet.
// ---------------------------------------------------------------

function ProjectPortal({ quote }: { quote: PublicQuoteResponse }) {
  const hasDeposit = quote.deposit_received_at != null;
  const status = quote.project_status;
  const scheduled = quote.project_scheduled_for;
  const visible = hasDeposit || status != null || scheduled != null;
  if (!visible) return null;

  const stages: { id: string; label: string; done: boolean; date: string | null }[] = [
    {
      id: "accepted",
      label: "Quote accepted",
      done: true,
      date: quote.accepted_at,
    },
    {
      id: "deposit",
      label: "Deposit received",
      done: hasDeposit,
      date: quote.deposit_received_at,
    },
    {
      id: "schedule",
      label: scheduled ? "Scheduled" : "Schedule",
      done: scheduled != null,
      date: scheduled,
    },
    {
      id: "in_progress",
      label: "Work in progress",
      done: status === "in_progress" || status === "complete",
      date: quote.project_started_at,
    },
    {
      id: "complete",
      label: "Complete",
      done: status === "complete",
      date: quote.project_completed_at,
    },
  ];

  return (
    <section className="mt-6 rounded-lg border border-rule bg-paper-warm p-5">
      <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Project status
      </h3>
      <ol className="mt-4 space-y-3">
        {stages.map((stage, i) => (
          <li key={stage.id} className="flex items-start gap-3">
            <span
              className={
                stage.done
                  ? "mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-graphite text-paper"
                  : "mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border border-rule bg-paper text-graphite-faint"
              }
            >
              {stage.done ? (
                <Check className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <span className="font-mono text-xs">{i + 1}</span>
              )}
            </span>
            <div className="flex-1">
              <p
                className={
                  stage.done ? "font-medium text-graphite" : "font-medium text-graphite-soft"
                }
              >
                {stage.label}
              </p>
              {stage.date && (
                <p className="font-mono text-[11px] text-graphite-soft">{formatTime(stage.date)}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
      {hasDeposit && quote.deposit_amount_cents != null && (
        <p className="mt-4 font-mono text-xs text-graphite-soft">
          Deposit on file:{" "}
          <span className="font-semibold text-graphite">
            ${(quote.deposit_amount_cents / 100).toFixed(2)}
          </span>
        </p>
      )}
    </section>
  );
}
